import { useEffectOnce } from '@dni-react-hooks/use-effect-once'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { Fade } from '@pets-ui/Fade/Fade'
import { ResponsiveDialog } from '@pets-ui/ResponsiveDialog/ResponsiveDialog'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import * as stylex from '@stylexjs/stylex'
import { useTranslation } from 'react-i18next'
import { usePopupTriggerService } from '../../hooks/usePopupTriggerService'
import { type PopupType } from '../../PopupContainer'
import { PopupActions } from '../PopupActions'
import { PopupContent } from '../PopupContent'
import { PopupHeader } from '../PopupHeader'

const baseStyle = stylex.create({
  checkboxContainer: {
    position: 'absolute',
    bottom: -28,
    left: 0,
  },
  backgroundHidden: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
  desktopDontShowTodayButton: (backgroundHidden: boolean) => ({
    position: 'absolute',
    bottom: backgroundHidden ? -38 : -32,
    left: backgroundHidden ? 8 : 0,
  }),
})

interface PopupDrawerProps {
  onClose?: () => void
  open: boolean
  popupKey?: string
  onSubmitPopupImpression?: () => void
  onSubmitPopupClickNoShow?: () => void
  title?: string
  content?: string
  image?: string
  linkUrl?: string
  buttonText?: string
  popupType?: PopupType
  width?: number
  backgroundHidden?: boolean
  onPopupClickEvent?: () => void
}

export const PopupDrawer = (props: PopupDrawerProps) => {
  const {
    onClose,
    open,
    popupKey,
    onSubmitPopupImpression,
    onSubmitPopupClickNoShow,
    title,
    content,
    image,
    linkUrl,
    buttonText,
    popupType,
    width,
    backgroundHidden,
    onPopupClickEvent,
    ...rest
  } = props
  const { suppressToday } = usePopupTriggerService()
  const { t } = useTranslation()
  const isMobile = useResponsive('mobile')

  useEffectOnce(() => {
    if (open && onSubmitPopupImpression) {
      onSubmitPopupImpression()
    }
  })

  const handleDontShowToday = () => {
    onSubmitPopupClickNoShow?.()
    suppressToday(popupKey || '')
    onClose?.()
  }

  return (
    <ResponsiveDialog
      open={open}
      xStyles={backgroundHidden
        ? {
          dialog: {

            paper: baseStyle.backgroundHidden,
          },
        }
        : undefined}
      {...rest}
    >
      <Fade in={open} duration={300}>
        <Box p="12px" pb={backgroundHidden ? 0 : '12px'}>
          <PopupHeader title={title || ''} onClose={onClose} backgroundHidden={!!backgroundHidden} />
          <PopupContent
            backgroundHidden={!!backgroundHidden}
            popupType={popupType}
            content={content || ''}
            image={image || ''}
            linkUrl={linkUrl || ''}
            width={width}
            onClick={onClose}
            onPopupClickEvent={onPopupClickEvent}
          />
          <PopupActions
            title={buttonText || ''}
            link={linkUrl || ''}
            onClick={onClose}
            onPopupClickEvent={onPopupClickEvent}
          />
          {!isMobile
          && (
            <Button
              variant="text"
              size="small"
              onClick={handleDontShowToday}
              data-testid="popup-dont-show"
              rootStyle={baseStyle.desktopDontShowTodayButton(!!backgroundHidden)}
            >
              <Text color={colors.white}>{t('popup.dontShowToday', { defaultValue: '今日不再顯示' })}</Text>
            </Button>
          )}
          {isMobile && (
            <Box pb={2} pt={1}>
              <Button variant="text" size="small" onClick={handleDontShowToday} data-testid="popup-dont-show">
                <Text color={backgroundHidden ? colors.white : colors.grey600}>{t('popup.dontShowToday', { defaultValue: '今日不再顯示' })}</Text>
              </Button>
            </Box>
          )}
        </Box>
      </Fade>
    </ResponsiveDialog>
  )
}
